/**
 * GenericContentSection — a plain heading + body text block.
 *
 * Props:
 *   heading   string — section heading
 *   body      string — multiline body text
 *   alignment 'left'|'center'|'right'   default 'left'
 *
 * Empty props render nothing.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RevealOnScroll from '../../components/RevealOnScroll';

const TEXT_ALIGN_MAP = {
  left: 'left',
  center: 'center',
  right: 'right',
};

const ALIGN_MAP = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

export default function GenericContentSection({ heading, body, alignment }) {
  const theme = useTheme();

  if (!heading && !body) return null;

  const textAlign = TEXT_ALIGN_MAP[alignment] || 'left';
  const alignItems = ALIGN_MAP[alignment] || 'flex-start';

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 9 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems,
            textAlign,
            gap: 2.5,
          }}
        >
          {heading && (
            <RevealOnScroll delay={0}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  lineHeight: 1.25,
                  color: 'text.primary',
                  letterSpacing: '-0.01em',
                }}
              >
                {heading}
              </Typography>
            </RevealOnScroll>
          )}

          {body && (
            <RevealOnScroll delay={0.08}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  fontSize: { xs: '1rem', md: '1.075rem' },
                  whiteSpace: 'pre-line',
                  maxWidth: textAlign === 'center' ? 680 : '100%',
                }}
              >
                {body}
              </Typography>
            </RevealOnScroll>
          )}
        </Box>
      </Container>
    </Box>
  );
}

GenericContentSection.propTypes = {
  heading: PropTypes.string,
  body: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
};

GenericContentSection.defaultProps = {
  alignment: 'left',
};
